import Calendar from "@/components/ui/Calendar";
import { RenderIconDismiss } from "@/components/ui/Icons/IconsCustom";
import { RootState } from "@/redux";
import { IOrganizationStore } from "@/redux/slices/organizations/OrganizationModel";
import { ResourceInfo, ResourceService } from "@/services";
import { MeetingDetails } from "@/services/resource-service/entity/MeeingEntity";
import { DateConverter, DateTimeFormatHelper } from "@/utilities";
import {
  CalendarDayProps,
  DEFAULT_CALENDAR_STRINGS,
} from "@fluentui/react-calendar-compat";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Spinner,
} from "@fluentui/react-components";
import { useQueries } from "@tanstack/react-query";
import { cloneDeep } from "lodash";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
// import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IResourceCalendarProps } from "./Models";
import {
  ResourceCalendarBaseWrapper,
  ResourceCalendarHeaderWrapper,
  RrfReservationContentChildWrapper,
  RrfReservationContentWrapper,
  RrfReservationListWrapper,
  RrfReservationSelectedDateWrapper,
  RrfReservationTblWrapper,
} from "./Styles";
import ScrollDismissTooltip from "@/components/ui/Tooltip";
import { FORMAT_DATE_DEFAULT, TimeFunction } from "@/common/datetime/TimeFunction";

const ResourceCalendar = forwardRef((props: IResourceCalendarProps, ref) => {
  // const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [resourceInfo, setResourceInfo] = useState<ResourceInfo | null>(null);
  const [monthChange, setMonthChange] = useState<string | null>(null);

  const theme: string = useSelector(
    (state: RootState) => state.settingInfo?.theme
  ) as string;

  useImperativeHandle(ref, () => ({
    onOpenResourceCalendar,
  }));

  const onOpenResourceCalendar = (resourceInfo: ResourceInfo, startDate: Date | null) => {
    setResourceInfo(cloneDeep(resourceInfo));
    setIsOpen(true);
    setSelectedDate(startDate || new Date());
  };

  const onDismiss = () => {
    setIsOpen(false);
    setResourceInfo(null);
  };

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date()
  ); // Initialize with today's date

  const handleDateChange = async (date: Date) => {
    setSelectedDate(date);
  };

  // const resourceQuery = useQuery<MeetingDetails[] | []>({
  //   queryKey: [resourceInfo?.email, selectedDate],
  //   queryFn: () => onFetchResource(resourceInfo?.email, selectedDate),
  //   staleTime: 20 * 1000,
  //   enabled: !!selectedDate && !!resourceInfo?.email,
  // });

  // const appointmentByMonth = useQuery<string[]>({
  //   queryKey: [resourceInfo?.email, monthChange],
  //   queryFn: () =>
  //     onFetchDateHaveAppointment(resourceInfo?.email, monthChange || ""),
  //   staleTime: 20 * 1000,
  //   enabled: !!monthChange && !!resourceInfo?.email,
  // });

  const results = useQueries({
    queries: [
      {
        queryKey: [resourceInfo?.email, selectedDate],
        queryFn: () => onFetchResource(resourceInfo?.email, selectedDate),
        staleTime: 20 * 1000,
        enabled: !!selectedDate && !!resourceInfo?.email,
      },
      {
        queryKey: [resourceInfo?.email, monthChange],
        queryFn: () =>
          onFetchDateHaveAppointment(resourceInfo?.email, monthChange || ""),
        staleTime: 20 * 1000,
        enabled: !!monthChange && !!resourceInfo?.email,
      },
    ],
  });

  const resourceQuery = results[0]; // Results for the first query
  const appointmentByMonth = results[1]; // Results for the second query

  const orgStore: IOrganizationStore = useSelector(
    (state: RootState) => state.orgInfo
  ) as IOrganizationStore;

  const onFetchResource = async (
    email: string | undefined,
    date: Date | undefined
  ): Promise<MeetingDetails[] | []> => {
    if (date && email) {
      const resourceServiceImpl: ResourceService = new ResourceService();

      const request: any = {
        tenantId: orgStore.orgInfo?.tenantId || "",
        orgId: orgStore?.orgInfo?.guid || "",
      };
      const response = await resourceServiceImpl.fetchMeetingByDate(
        request,
        `${TimeFunction.dateTimeFormatter(date, FORMAT_DATE_DEFAULT)}T00:00:00.000Z`,
        `${TimeFunction.dateTimeFormatter(date, FORMAT_DATE_DEFAULT)}T23:59:59.000Z`,
        resourceInfo?.guid || ""
      );
      return response.data;
    }
    return [];
  };

  const onFetchDateHaveAppointment = async (
    email: string | undefined,
    month: string
  ): Promise<string[]> => {
    if (email) {
      const dateString = `${month}T00:00:00`;
      const resourceServiceImpl: ResourceService = new ResourceService();

      const request: any = {
        tenantId: orgStore.orgInfo?.tenantId || "",
        orgId: orgStore?.orgInfo?.guid || "",
      };
      const response = await resourceServiceImpl.fetchMeetingDaysByMonth(
        request,
        dateString,
        resourceInfo?.guid || ""
      );
      return response.data;
    }
    return [];
  };

  const calendarDayProps: Partial<CalendarDayProps> = {
    getMarkedDays: useCallback(
      (startingDate: Date, endDate: Date) => {
        const midpoint: Date = new Date(
          (startingDate.getTime() + endDate.getTime()) / 2
        );
        const monthNumber = (midpoint.getMonth() || 0) + 1;
        const monthFormat = monthNumber > 9 ? monthNumber : `0${monthNumber}`;
        const month = `${midpoint.getFullYear()}-${monthFormat}-15`;
        console.log(month);
        if (month !== monthChange) {
          setMonthChange(month);
        }
        return (appointmentByMonth.data || []).map((item) => new Date(item));
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [JSON.stringify(appointmentByMonth.data)]
    ),
  };

  const mapStatusToColor = (status: string, isNotOrg: boolean) => {
    const commonCss: string = "rc-attendee-meeting-info-status";
    let ownCss: string = "";
    switch (status?.toLowerCase()) {
      case "free":
        ownCss = "status-free";
        break;
      case "tentative":
        ownCss = "status-tentative";
        break;
      case "busy":
        ownCss = isNotOrg ? "status-busy" : "status-own";
        //  ownCss = "status-busy";
        break;
      case "outofoffice":
      case "away":
        ownCss = "status-outOfOffice";
        break;
      case "workingelsewhere":
        ownCss = "status-workingElsewhere";
        break;
      case "nodata":
        ownCss = "status-noData";
        break;
      case "own":
        ownCss = "status-own";
        break;
    }

    return commonCss + " " + ownCss;
  }

  return (
    <Drawer separator open={isOpen} size="full">
      {resourceInfo ? (
        <>
          <DrawerHeader>
            <DrawerHeaderTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<RenderIconDismiss theme={theme} />}
                  onClick={onDismiss}
                />
              }
            >
              <div className="header_custom">
                <div
                >
                  <ScrollDismissTooltip content={resourceInfo.displayName || ""} relationship="label">
                    <ResourceCalendarHeaderWrapper >
                      <span className="resourceCalendarHeader truncate-text">{resourceInfo.displayName}</span>
                    </ResourceCalendarHeaderWrapper>
                  </ScrollDismissTooltip>

                </div>
              </div>

            </DrawerHeaderTitle>
          </DrawerHeader>
          <DrawerBody>
            <ResourceCalendarBaseWrapper className="ResourceCalendarBaseWrapper">

              <Calendar
                isDayPickerVisible={true}
                showGoToToday={true}
                isMonthPickerVisible={false}
                value={selectedDate}
                firstDayOfWeek={props.firstDayOfWeek || 0}
                onSelectDate={(date) => {
                  handleDateChange(date);
                }}
                showMonthPickerAsOverlay={true}
                highlightSelectedMonth={true}
                allFocusable={true}
                strings={DEFAULT_CALENDAR_STRINGS}
                calendarDayProps={calendarDayProps}
              />
              <RrfReservationContentWrapper>
                <RrfReservationSelectedDateWrapper>
                  <p>{DateTimeFormatHelper.FormatByCulture(selectedDate, props.dateFormat || undefined)}</p>
                </RrfReservationSelectedDateWrapper>
                <RrfReservationListWrapper className="rf-reservation-list">
                  <div>
                    <RrfReservationContentChildWrapper>
                      <RrfReservationTblWrapper>
                        {resourceQuery.isLoading ? (
                          <Spinner size="small" />
                        ) : (
                          <table className="rf-reservation-tbl">
                            <tbody>
                              {resourceQuery &&
                                resourceQuery.data &&
                                resourceQuery.data.map(
                                  (item: MeetingDetails) => {
                                    const email = item.externalProperty ? item.externalProperty?.address : item.organizer?.emailAddress?.address;
                                    const isNotOrg = email?.toLowerCase() != orgStore?.organizer?.email?.toLowerCase();

                                    // const statusColor = item.timelineStatus === "tentative"
                                    //   ? "#ff0000" // Red color for tentative status
                                    //   : isNotOrg
                                    //     ? "#0b8aff"
                                    //     : "#937bd1";

                                    const from =
                                      DateTimeFormatHelper.FormatByCulture(
                                        new Date(item.startTime.dateTime),
                                        props.timeFormat || undefined,
                                      );
                                    const to =
                                      DateTimeFormatHelper.FormatByCulture(
                                        new Date(item.endTime.dateTime),
                                        props.timeFormat || undefined,
                                      );

                                    return (
                                      <tr key={item.guid} title={item.subject}>
                                        <td style={{ width: "1%" }}>
                                          {from} <br /> {to}
                                        </td>
                                        <td
                                          className={mapStatusToColor(item.showAs || "", isNotOrg)}
                                          style={{
                                            width: "1px",
                                            // backgroundColor: statusColor,
                                            padding: "1.5px",
                                          }}
                                        ></td>
                                        <td>
                                          <div className="rf-resource-calendar-info">
                                            <b>{item.subject}</b>
                                          </div>
                                          <div className="rf-resource-calendar-info">
                                            <a
                                              className=""
                                              href={
                                                "mailto:" +
                                                  item.externalProperty ? item.externalProperty?.address : item.organizer?.emailAddress?.address
                                              }
                                            >
                                              {
                                                item.externalProperty ? item.externalProperty?.address : item.organizer?.emailAddress?.address
                                              }
                                            </a>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                            </tbody>
                          </table>
                        )}
                      </RrfReservationTblWrapper>
                    </RrfReservationContentChildWrapper>
                  </div>
                </RrfReservationListWrapper>
              </RrfReservationContentWrapper>
            </ResourceCalendarBaseWrapper>
          </DrawerBody>
        </>
      ) : null}
    </Drawer>
  );
});

export default ResourceCalendar;
